import { motion } from "framer-motion";
import { ShieldCheck, Gauge, MapPin, Wrench, ArrowRight } from "lucide-react";
import Link from "next/link";

const WhyChooseKasai = () => {
  const cardBase =
    "group relative overflow-hidden rounded-2xl p-6 sm:p-7 flex flex-col justify-between transition-all duration-300 gap-6";
  const darkCard =
    "bg-gradient-to-br from-card/[0.06] to-card/[0.02] border border-card/10 hover:border-card/25";
  const accentCard =
    "bg-gradient-to-br from-primary to-primary/70 border border-primary/40 text-primary-foreground gap-6";

  const IconBox = ({ children, accent = false }: { children: React.ReactNode; accent?: boolean }) => (
   <div
  className={`w-11 h-11 min-w-11 min-h-11 shrink-0 flex-none rounded-lg flex items-center justify-center ${
    accent
      ? "bg-primary-foreground/15 border border-primary-foreground/25"
      : "bg-card/5 border border-card/15 group-hover:border-card/30"
  }`}
>
  {children}
</div>
  );

  return (
    <section className="relative py-16 sm:py-20 bg-foreground text-card overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--card)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--card)) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 max-w-2xl"
        >
          <span className="text-secondary font-bold text-xs tracking-[0.3em] uppercase">
            The Dealer Builders Trust
          </span>
          <h2 className="font-display text-3xl sm:text-4xl mt-4 leading-[0.95]">
            Why Choose <span className="text-primary">Kasai</span>
          </h2>
          <p className="text-card/60 mt-4 text-base sm:text-lg">
           The Dealer Builder Trust
          </p>
        </motion.div>

        {/* Bento grid — matches reference */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-4 ">
          {/* Tall left card — row-span 2 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={`${cardBase} ${darkCard} lg:row-span-2`}
          >
            <IconBox>
              <ShieldCheck className="w-5 h-5 text-card" strokeWidth={1.6} />
            </IconBox>
            <div>
              <h3 className="font-display text-[20px] leading-[0.95]">
                Genuine Machinery
              </h3>
              <p className="text-card/60 mt-3 text-sm leading-relaxed max-w-xs">
                We provide authentic, high-performance machinery designed for reliability, efficiency, and long-term operation.
              </p>
            </div>
          </motion.div>

          {/* Top middle — dark */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className={`${cardBase} ${darkCard}`}
          >
            <IconBox>
              <Gauge className="w-5 h-5 text-card" strokeWidth={1.6} />
            </IconBox>
            <div>
              <h3 className="font-display text-[20px] leading-tight">
               Authorized Engirise Dealer 
              </h3>
              <p className="text-card/60 mt-2 text-sm leading-relaxed">
                Official supplier of certified Engirise equipment with trusted manufacturer-backed support. 
              </p>
            </div>
          </motion.div>

          {/* Top right — ACCENT highlighted */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className={`${cardBase} ${accentCard}`}
          >
            <div
              aria-hidden
              className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary-foreground/10 blur-2xl"
            />
            <IconBox accent>
              <MapPin className="w-5 h-5 text-primary-foreground" strokeWidth={1.6} />
            </IconBox>
            <div className="relative">
              <h3 className="font-display text-[20px] leading-tight">
                Spare Parts Availability 
              </h3>
              <p className="text-primary-foreground/80 mt-2 text-sm leading-relaxed">
                Genuine spare parts are readily available to minimize downtime and ensure uninterrupted operations. 
              </p>
            </div>
          </motion.div>

          {/* Bottom — wide card spanning 2 cols */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className={`${cardBase} ${darkCard} md:col-span-2`}
          >
            <IconBox>
              <Wrench className="w-5 h-5 text-card" strokeWidth={1.6} />
            </IconBox>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h3 className="font-display text-[20px] leading-tight">
                  After-Sales Support 
                </h3>
                <p className="text-card/60 mt-2 text-sm leading-relaxed max-w-md">
                  Dedicated service engineers deliver prompt technical assistance and ongoing maintenance support. 
                </p>
              </div>
              <Link
                href="/services"
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-card/80 hover:text-primary transition-colors self-start sm:self-auto"
              >
                Explore <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseKasai;
